/**
 * Adding or changing an approved cluster (D447): a sheet from the trailing edge. A cluster is named by its address (or its
 * Azure resource), never by a kubeconfig, a token or a certificate: the sign-in the work runs as is each person's own, or an
 * organisation secret kept under Secrets and named here. Node pools say which nodes belong to them (their tags) and which
 * runtime classes they offer; the projects ticked are the ones the pools serve.
 */

import type { ApprovedCluster, ApprovedClusterInput, ApprovedNodePool } from '@aioe/shared';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';

import { computeRoutes, problemText, routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, Tick } from '../ui.tsx';
import { FormField, FormSheet } from '../ui/FormSheet.tsx';
import { Chips } from '../manifestClauses.tsx';
import { Block } from '../foundry/FormBlock.tsx';

const NO_SECRET = '__none';
const isHttps = (v: string) => {
  try {
    const u = new URL(v);
    return u.protocol === 'https:' && !u.username && !u.password && !u.search && !u.hash;
  } catch {
    return false;
  }
};

interface PoolDraft {
  name: string;
  namespace: string;
  tags: string[];
  runtimeClasses: string[];
  /** Kept as it was when editing. */
  quota?: ApprovedNodePool['quota'];
}

const blankPool = (): PoolDraft => ({ name: '', namespace: '', tags: [], runtimeClasses: [] });

export function ClusterForm({ cluster, onClose, onSaved }: { cluster?: ApprovedCluster; onClose: () => void; onSaved: () => void }) {
  const editing = Boolean(cluster);
  const [kind, setKind] = useState<ApprovedCluster['kind']>(cluster?.kind ?? 'kubernetes');
  const [name, setName] = useState(cluster?.name ?? '');
  const [server, setServer] = useState(cluster?.server ?? '');
  const [resourceId, setResourceId] = useState(cluster?.aks?.resourceId ?? cluster?.azureLocal?.resourceId ?? '');
  const [region, setRegion] = useState(cluster?.aks?.region ?? '');
  const [localName, setLocalName] = useState(cluster?.azureLocal?.cluster ?? '');
  const [namespaces, setNamespaces] = useState<string[]>(cluster?.namespaces ?? []);
  const [pools, setPools] = useState<PoolDraft[]>(() =>
    (cluster?.nodePools ?? []).map((p) => ({ name: p.name, namespace: p.namespace ?? '', tags: p.tags.filter((t) => t !== `pool:${p.name}`), runtimeClasses: [...(p.runtimeClasses ?? [])], ...(p.quota ? { quota: p.quota } : {}) })),
  );
  const [identity, setIdentity] = useState<ApprovedCluster['identity']>(cluster?.identity ?? 'person');
  const [secret, setSecret] = useState(cluster?.organisationSecret ?? NO_SECRET);
  const [projectIds, setProjectIds] = useState<string[]>(cluster?.projectIds ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const secrets = useLoad(() => routes.secrets(), []);
  const projects = useLoad(() => routes.projects(), []);

  const kept = (secrets.data?.secrets ?? []).filter((s) => s.scope === 'organisation').map((s) => s.name);
  if (secret !== NO_SECRET && !kept.includes(secret)) kept.push(secret);
  const secretOptions: Record<string, string> = { [NO_SECRET]: kept.length ? 'None chosen' : 'No organisation secrets kept yet', ...Object.fromEntries(kept.sort().map((n) => [n, n])) };

  const setPool = (i: number, patch: Partial<PoolDraft>) => setPools((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const poolName = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
  const problem = !name.trim()
    ? 'Give the cluster a name.'
    : kind === 'kubernetes' && !isHttps(server.trim())
      ? 'The cluster needs its https address, with no user name, password or query.'
      : kind === 'aks' && (!resourceId.trim() || !region.trim())
        ? 'An AKS cluster needs its Azure resource id and its region.'
        : kind === 'azure-local' && !localName.trim()
          ? 'An Azure Local cluster needs its name.'
          : pools.some((p) => !poolName.test(p.name.trim()))
            ? 'Each node pool needs a lower-case name: letters, digits and dashes.'
            : undefined;

  const save = async () => {
    setBusy(true);
    setError(undefined);
    const body: ApprovedClusterInput = {
      kind,
      name: name.trim(),
      identity,
      ...(identity === 'organisation' && secret !== NO_SECRET ? { organisationSecret: secret } : {}),
      ...(kind === 'kubernetes' ? { server: server.trim() } : {}),
      ...(kind === 'aks' ? { aks: { resourceId: resourceId.trim(), region: region.trim() } } : {}),
      ...(kind === 'azure-local' ? { azureLocal: { cluster: localName.trim(), ...(resourceId.trim() ? { resourceId: resourceId.trim() } : {}) } } : {}),
      ...(namespaces.length ? { namespaces } : {}),
      ...(pools.length
        ? { nodePools: pools.map((p): ApprovedNodePool => ({ name: p.name.trim(), tags: p.tags, ...(p.namespace.trim() ? { namespace: p.namespace.trim() } : {}), ...(p.runtimeClasses.length ? { runtimeClasses: p.runtimeClasses } : {}), ...(p.quota ? { quota: p.quota } : {}) })) }
        : {}),
      ...(cluster?.foundryConnectionId ? { foundryConnectionId: cluster.foundryConnectionId } : {}),
      ...(projectIds.length ? { projectIds } : {}),
    };
    try {
      if (cluster) await computeRoutes.saveCluster(cluster.id, body);
      else await computeRoutes.addCluster(body);
      onSaved();
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };

  return (
    <FormSheet
      width="xl"
      title={editing ? `Edit ${cluster!.name}` : 'Add a cluster'}
      description="Once approved, work for the projects ticked here may run on the cluster’s node pools. The organisation’s policy is rewritten to match when you save."
      onClose={() => !busy && onClose()}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onPress={() => void save()} isPending={busy} isDisabled={Boolean(problem)}>
            {editing ? 'Save' : 'Add cluster'}
          </Button>
        </>
      }
    >
      {!editing ? (
        <Block label="Where it runs">
          <Segment aria-label="Where it runs" size="sm" selectedKey={kind} onSelectionChange={(k) => setKind(k === 'aks' ? 'aks' : k === 'azure-local' ? 'azure-local' : 'kubernetes')} className="w-fit max-w-full">
            <Segment.Item id="kubernetes">Kubernetes</Segment.Item>
            <Segment.Item id="aks">Azure (AKS)</Segment.Item>
            <Segment.Item id="azure-local">Azure Local</Segment.Item>
          </Segment>
        </Block>
      ) : null}
      <FormField label="Name" hint="What people and administrators call this cluster.">
        <input className={inputClass} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
      </FormField>

      {kind === 'kubernetes' ? (
        <FormField label="API server address" hint="Only the address. The platform never keeps a kubeconfig, a token or a certificate.">
          <input className={`${inputClass} mono`} value={server} spellCheck={false} onChange={(e) => setServer(e.target.value)} />
        </FormField>
      ) : null}
      {kind === 'aks' ? (
        <>
          <FormField label="Azure resource id" hint="The cluster’s resource id in Azure.">
            <input className={`${inputClass} mono`} value={resourceId} spellCheck={false} onChange={(e) => setResourceId(e.target.value)} />
          </FormField>
          <FormField label="Region" hint="For example australiaeast.">
            <input className={inputClass} value={region} maxLength={60} onChange={(e) => setRegion(e.target.value)} />
          </FormField>
        </>
      ) : null}
      {kind === 'azure-local' ? (
        <>
          <FormField label="Cluster name">
            <input className={inputClass} value={localName} maxLength={100} onChange={(e) => setLocalName(e.target.value)} />
          </FormField>
          <FormField label="Azure resource id (optional)">
            <input className={`${inputClass} mono`} value={resourceId} spellCheck={false} onChange={(e) => setResourceId(e.target.value)} />
          </FormField>
        </>
      ) : null}

      <Block label="Namespaces" hint="Where agent pods may live. Press Enter after each one.">
        <Chips items={namespaces} onChange={setNamespaces} />
      </Block>

      <Block label="Node pools" hint="Nodes join a pool with a key made for it, and carry its tags. Every pool also carries pool:<its name>.">
        <div className="grid gap-2">
          {pools.map((p, i) => (
            <div key={i} className="grid gap-3 rounded-lg border border-separator p-3">
              <FormField label="Pool name" hint="Lower-case letters, digits and dashes.">
                <input className={inputClass} value={p.name} maxLength={63} spellCheck={false} onChange={(e) => setPool(i, { name: e.target.value })} />
              </FormField>
              <FormField label="Namespace (optional)" hint="Left empty, the first namespace above.">
                <input className={inputClass} value={p.namespace} maxLength={63} spellCheck={false} onChange={(e) => setPool(i, { namespace: e.target.value })} />
              </FormField>
              <Block label="Tags" hint="Press Enter after each one.">
                <Chips items={p.tags} onChange={(v) => setPool(i, { tags: v })} />
              </Block>
              <Block label="Runtime classes" hint="The isolation classes this pool offers, such as gvisor or kata.">
                <Chips items={p.runtimeClasses} onChange={(v) => setPool(i, { runtimeClasses: v })} />
              </Block>
              <div>
                <Button size="sm" variant="ghost" onPress={() => setPools((ps) => ps.filter((_, j) => j !== i))}>
                  Remove this pool
                </Button>
              </div>
            </div>
          ))}
          <div>
            <Button size="sm" variant="secondary" onPress={() => setPools((ps) => [...ps, blankPool()])}>
              Add a pool
            </Button>
          </div>
        </div>
      </Block>

      <Block label="Projects it serves" hint="Work for a ticked project that needs a cluster runs only on this cluster’s nodes. Tick none to serve any project.">
        <div className="grid gap-2">
          {(projects.data ?? []).map((p) => (
            <Tick key={p.id} checked={projectIds.includes(p.id)} onChange={(v) => setProjectIds((ids) => (v ? [...ids, p.id] : ids.filter((x) => x !== p.id)))}>
              {p.name}
            </Tick>
          ))}
          {projects.data && !projects.data.length ? <p className="text-xs text-muted">There are no projects yet.</p> : null}
        </div>
      </Block>

      <Block label="Whose sign-in the work runs as">
        <Segment aria-label="Whose sign-in the work runs as" size="sm" selectedKey={identity} onSelectionChange={(k) => setIdentity(k === 'organisation' ? 'organisation' : 'person')} className="w-fit max-w-full">
          <Segment.Item id="person">Each person’s own</Segment.Item>
          <Segment.Item id="organisation">The organisation’s</Segment.Item>
        </Segment>
      </Block>
      {identity === 'organisation' ? (
        <Block label="Organisation secret" hint="The secret nodes sign in with. Keep it under Secrets, at organisation scope; only its name is stored here.">
          <ChoiceSelect label="Organisation secret" className="w-full" value={secret} options={secretOptions} onChange={setSecret} />
        </Block>
      ) : null}
      {problem && (name || server || resourceId || localName || pools.length) ? <p className="text-xs text-muted">{problem}</p> : null}
      <ErrorNote error={error} />
    </FormSheet>
  );
}
